import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, LucideIcon } from "lucide-react";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";

type CTAAction = 'cta_click_signup' | 'cta_click_calendly' | 'cta_click_login' | 'cta_click_3p_compare';

interface StickyMobileCTAProps {
  href?: string;
  label?: string;
  ariaLabel?: string;
  icon?: LucideIcon;
  trackingId?: CTAAction;
  trackingLocation?: string;
  variant?: "default" | "gold";
}

export const StickyMobileCTA = ({
  href = "https://taxes.elyx-finance.ch/auth?tab=signup",
  label = "Créer mon espace sécurisé",
  ariaLabel = "Créer mon espace sécurisé Easy Taxes (ouvre dans un nouvel onglet)",
  icon: Icon = Lock,
  trackingId = "cta_click_signup",
  trackingLocation = "sticky_mobile",
  variant = "default"
}: StickyMobileCTAProps) => {
  const { trackCTA } = useGoogleAnalytics();
  const [isVisible, setIsVisible] = useState(false);
  const [isNearFooter, setIsNearFooter] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show after scrolling 400px
      const shouldShow = scrollY > 400;
      
      // Hide when near footer (last 300px of page)
      const nearFooter = scrollY + windowHeight > documentHeight - 300;
      
      setIsVisible(shouldShow && !nearFooter);
      setIsNearFooter(nearFooter);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Check initial state
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const buttonClasses = variant === "gold"
    ? "px-6 min-h-[48px] text-sm font-semibold bg-gold-primary hover:bg-gold-light text-white shadow-[0_4px_20px_rgba(186,149,95,0.35)] rounded-xl focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2"
    : "px-6 min-h-[48px] text-sm font-semibold bg-green-primary hover:bg-green-primary/90 text-white shadow-[0_4px_20px_rgba(11,59,50,0.35)] rounded-xl focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2";

  return (
    <div 
      role="region"
      aria-label="Inscription rapide"
      aria-hidden={!isVisible}
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 md:hidden safe-area-inset transition-all duration-300 ease-out flex justify-center ${
        isVisible 
          ? "translate-y-0 opacity-100" 
          : "translate-y-full opacity-0 pointer-events-none"
      }`}
    >
      <Button
        size="lg"
        className={buttonClasses}
        asChild
        tabIndex={isVisible ? 0 : -1}
      >
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2"
          aria-label={ariaLabel}
          onClick={() => trackCTA(trackingId, trackingLocation, 'sticky')}
        >
          <Icon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
          {label}
        </a>
      </Button>
    </div>
  );
};
